import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import {
  X, Send, Loader2, Paperclip, Bold, Italic, Underline, List, ListOrdered,
  AlertTriangle, FileText, Trash2, Eye, Download, AArrowDown, AArrowUp,
} from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((v || "").trim());
const MAX_CC = 3;

const ToolBtn = ({ icon: Icon, label, onClick, testid }) => (
  <button type="button" onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    title={label} data-testid={testid}
    className="p-1.5 rounded-lg text-[#4A4B4A] hover:bg-[#F3F2EE] transition-colors">
    <Icon className="w-3.5 h-3.5" />
  </button>
);

/**
 * LetterComposer - write, preview, confirm and send a clinical letter for a patient.
 * The email itself only carries a secure link; the letter content lives in the PDF/secure page.
 */
const fillPlaceholders = (text, ctx) =>
  (text || "").replace(/\{(\w+)\}/g, (m, key) => (ctx[key] != null && ctx[key] !== "" ? ctx[key] : m));

export default function LetterComposer({ patient, clinic, user, appointment, letter, onClose, onSent }) {
  // An existing draft is edited in place - saving never creates a second record
  const [letterId, setLetterId] = useState(letter?.id || "");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(letter?.template_key || "");
  const [subject, setSubject] = useState(letter?.subject || "");
  const [to, setTo] = useState(letter?.recipient_email || patient?.email || "");
  const [recipientName, setRecipientName] = useState(letter?.recipient_name || "");
  const [cc, setCc] = useState((letter?.cc_emails || []).join(", "));
  const [attachments, setAttachments] = useState(letter?.attachments || []);
  const [bodyHtml, setBodyHtml] = useState(letter?.body_html || "");
  const [savedNote, setSavedNote] = useState("");
  const [stage, setStage] = useState("compose"); // compose | preview | confirm
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const bodyRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (stage === "compose" && bodyRef.current && bodyHtml) bodyRef.current.innerHTML = bodyHtml;
  }, [stage, bodyHtml]);

  const placeholderCtx = {
    patient_name: `${patient?.first_name || ""} ${patient?.last_name || ""}`.trim(),
    patient_dob: patient?.date_of_birth || "",
    clinic_name: clinic?.name || "",
    practitioner_name: user?.full_name || user?.name || "",
    today_date: new Date().toLocaleDateString("en-GB"),
    appointment_date: appointment?.date || "",
    appointment_type: appointment?.appointment_type_name || "",
  };

  const applyTemplate = (t) => {
    setTemplateId(t.id);
    setSubject(fillPlaceholders(t.subject, placeholderCtx));
    const html = fillPlaceholders(t.body_html, placeholderCtx);
    setBodyHtml(html);
    if (bodyRef.current) bodyRef.current.innerHTML = html;
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/letter-templates");
        setTemplates(data.templates || []);
        // Only seed from a template for a brand-new letter - never overwrite a saved draft
        if (letter?.id) return;
        const first = (data.templates || []).find((t) => t.is_default) || (data.templates || [])[0];
        if (first) applyTemplate(first);
      } catch {
        setError("Letter templates could not be loaded. You can still write a letter.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    const typed = (bodyRef.current?.innerText || "").trim();
    if (typed && !window.confirm("Changing template will replace the current letter content. Continue?")) return;
    applyTemplate(t);
  };

  const changeFontSize = (dir) => {
    bodyRef.current?.focus();
    const current = Number(document.queryCommandValue("fontSize")) || 3;
    const next = Math.min(6, Math.max(1, current + dir));
    document.execCommand("fontSize", false, String(next));
  };

  const format = (cmd, value) => {
    bodyRef.current?.focus();
    document.execCommand(cmd, false, value);
  };

  const ccList = cc.split(",").map((e) => e.trim()).filter(Boolean);
  const invalidCc = ccList.filter((e) => !isEmail(e));
  const ccError = invalidCc.length
    ? `${invalidCc.length > 1 ? "These addresses are" : "This address is"} not valid: ${invalidCc.join(", ")}`
    : ccList.length > MAX_CC
      ? `You can add up to ${MAX_CC} CC recipients.`
      : "";

  const validate = () => {
    if (!subject.trim()) return "Please add a subject for this letter.";
    if (!isEmail(to)) return "Please enter a valid recipient email address.";
    if (ccError) return ccError;
    if (!(bodyRef.current?.innerText || "").trim()) return "Please write the letter content.";
    return "";
  };

  const uploadFile = async (file) => {
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/patients/${patient.id}/letters/attachments`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((a) => [...a, data]);
    } catch (err) {
      setError(err?.response?.data?.detail || "That file could not be attached.");
    }
    setBusy(false);
  };

  const send = async () => {
    setBusy(true);
    setError("");
    try {
      const saved = await persist("ready");
      const { data: result } = await api.post(`/letters/${saved.id}/send`, {});
      if (result.status !== "sent") {
        setError(result.note || "The letter could not be emailed. Please try again, or contact support if it keeps failing.");
        setBusy(false);
        onSent?.();
        return;
      }
      onSent?.(result.letter);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail?.message || (typeof detail === "string" ? detail : "The letter could not be sent."));
    }
    setBusy(false);
  };

  const payload = (status) => ({
    subject: subject.trim() || "Letter",
    body_html: bodyRef.current?.innerHTML || bodyHtml || "",
    recipient_email: isEmail(to) ? to.trim() : null,
    recipient_name: recipientName.trim() || null,
    cc_emails: ccList.filter(isEmail),
    attachments,
    template_key: templateId,
    appointment_id: appointment?.id || letter?.appointment_id || null,
    status,
  });

  // Update when we already have a record, insert only the first time
  const persist = async (status) => {
    if (letterId) {
      const { data } = await api.put(`/letters/${letterId}`, payload(status));
      return data;
    }
    const { data } = await api.post(`/patients/${patient.id}/letters`, payload(status));
    setLetterId(data.id);
    return data;
  };

  const saveDraft = async () => {
    setBusy(true);
    setError("");
    setSavedNote("");
    try {
      const saved = await persist("draft");
      setSavedNote(`Draft saved at ${new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`);
      onSent?.(saved);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "The draft could not be saved.");
    }
    setBusy(false);
  };

  const download = async () => {
    setBusy(true);
    setError("");
    try {
      const saved = await persist("draft");
      const res = await api.get(`/letters/${saved.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(subject || "Letter").replace(/[^\w-]+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      onSent?.();
    } catch {
      setError("PDF could not be generated. Please try again or contact support.");
    }
    setBusy(false);
  };

  const patientName = `${patient?.first_name || ""} ${patient?.last_name || ""}`.trim();

  return (
    <div className="fixed inset-0 z-[60] bg-[#FAF9F6] flex flex-col" data-testid="letter-composer">
      <div className="bg-white px-4 sm:px-6 py-3 flex items-center gap-3 border-b border-[#1A1B1A]/[0.06]">
        <div>
          <h2 className="text-base text-[#1A1B1A]" style={H}>{letterId ? "Edit draft letter" : "New letter"}</h2>
          <p className="text-xs text-[#757675]">For {patientName || "this patient"}</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {savedNote && (
            <span className="text-[10px] text-[#205B5A] mr-1" data-testid="letter-saved-note">{savedNote}</span>
          )}
          <button onClick={saveDraft} disabled={busy} data-testid="letter-save-draft-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-xs text-[#1A1B1A] hover:bg-[#F3F2EE] disabled:opacity-50">
            <FileText className="w-3.5 h-3.5" /> Save draft
          </button>
          <button onClick={download} disabled={busy} data-testid="letter-download-pdf-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-xs text-[#1A1B1A] hover:bg-[#F3F2EE] disabled:opacity-50">
            <Download className="w-3.5 h-3.5" /> Download PDF
          </button>
          {stage === "compose" ? (
            <button
              onClick={() => {
                const html = bodyRef.current?.innerHTML || "";
                const e = validate();
                if (e) { setError(e); return; }
                setBodyHtml(html);
                setError("");
                setStage("preview");
              }}
              data-testid="letter-preview-btn"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#205B5A] text-white text-xs font-medium hover:bg-[#174544]">
              <Eye className="w-3.5 h-3.5" /> Preview
            </button>
          ) : (
            <button onClick={() => setStage("compose")} data-testid="letter-back-to-edit-btn"
              className="px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-xs text-[#1A1B1A] hover:bg-[#F3F2EE]">
              Back to editing
            </button>
          )}
          <button onClick={onClose} data-testid="letter-close-btn"
            className="p-2 rounded-xl border border-[#1A1B1A]/10 text-[#4A4B4A] hover:bg-[#F3F2EE]">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3" data-testid="letter-error">
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}

          {stage === "compose" && (
            <>
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-4 space-y-3">
                <div>
                  <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider">Template</label>
                  <select value={templateId} onChange={(e) => chooseTemplate(e.target.value)}
                    data-testid="letter-template-select"
                    className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm bg-white">
                    {!templates.some((t) => t.id === templateId) && (
                      <option value="">No template</option>
                    )}
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <p className="text-[10px] text-[#757675] mt-1">Manage templates in Settings → Letters & emails</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider">To</label>
                    <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@example.com"
                      data-testid="letter-to-input"
                      className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider">Recipient name (optional)</label>
                    <input value={recipientName} onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="Dr Smith" data-testid="letter-recipient-name-input"
                      className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider">CC <span className="normal-case tracking-normal text-[#9A9B9A]">optional</span></label>
                  <input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="gp@surgery.example, consultant@example.com"
                    data-testid="letter-cc-input"
                    className={`mt-1 w-full px-3 py-2.5 rounded-xl border text-sm ${ccError ? "border-red-300 bg-red-50/40" : "border-[#1A1B1A]/10"}`} />
                  {ccError ? (
                    <p className="text-[10px] text-red-600 mt-1" data-testid="letter-cc-error">{ccError}</p>
                  ) : (
                    <p className="text-[10px] text-[#757675] mt-1">Separate up to {MAX_CC} email addresses with a comma.</p>
                  )}
                  <p className="text-[10px] text-[#757675] mt-0.5">Each CC recipient will receive their own secure copy of the letter link.</p>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider">Subject</label>
                  <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Referral"
                    data-testid="letter-subject-input"
                    className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" />
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
                <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-[#1A1B1A]/[0.06] bg-[#FAF9F6]">
                  <ToolBtn icon={Bold} label="Bold" onClick={() => format("bold")} testid="letter-bold-btn" />
                  <ToolBtn icon={Italic} label="Italic" onClick={() => format("italic")} testid="letter-italic-btn" />
                  <ToolBtn icon={Underline} label="Underline" onClick={() => format("underline")} testid="letter-underline-btn" />
                  <span className="w-px h-4 bg-[#1A1B1A]/10 mx-1" />
                  <select
                    onChange={(e) => { format("formatBlock", e.target.value); e.target.selectedIndex = 0; }}
                    data-testid="letter-text-style-select"
                    className="text-[11px] px-2 py-1 rounded-lg border border-[#1A1B1A]/10 bg-white text-[#4A4B4A]"
                  >
                    <option value="">Text style</option>
                    <option value="<p>">Normal text</option>
                    <option value="<h2>">Heading</option>
                    <option value="<h3>">Subheading</option>
                  </select>
                  <ToolBtn icon={AArrowDown} label="Smaller text" onClick={() => changeFontSize(-1)} testid="letter-font-smaller-btn" />
                  <ToolBtn icon={AArrowUp} label="Larger text" onClick={() => changeFontSize(1)} testid="letter-font-larger-btn" />
                  <span className="w-px h-4 bg-[#1A1B1A]/10 mx-1" />
                  <ToolBtn icon={List} label="Bullet list" onClick={() => format("insertUnorderedList")} testid="letter-bullets-btn" />
                  <ToolBtn icon={ListOrdered} label="Numbered list" onClick={() => format("insertOrderedList")} testid="letter-numbers-btn" />
                </div>
                <div
                  ref={bodyRef}
                  contentEditable
                  suppressContentEditableWarning
                  data-testid="letter-body-editor"
                  className="min-h-[300px] px-4 sm:px-5 py-4 text-sm text-[#1A1B1A] leading-relaxed focus:outline-none [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-1 [&_p]:mb-2 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5"
                />
              </div>

              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Paperclip className="w-3.5 h-3.5 text-[#757675]" />
                  <span className="text-xs font-medium text-[#1A1B1A]">Attachments</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="letter-upload-btn"
                    className="text-[10px] px-2.5 py-1.5 rounded-lg border border-[#1A1B1A]/10 hover:bg-[#F3F2EE] disabled:opacity-50">
                    Upload file
                  </button>
                  <input ref={fileRef} type="file" multiple className="hidden"
                    onChange={(e) => Array.from(e.target.files || []).forEach(uploadFile)} />
                </div>
                {attachments.length === 0 ? (
                  <p className="text-[10px] text-[#757675]">No attachments yet.</p>
                ) : (
                  <div className="space-y-1.5" data-testid="letter-attachment-list">
                    {attachments.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-[#1A1B1A] bg-[#FAF9F6] rounded-lg px-3 py-2">
                        <FileText className="w-3 h-3 text-[#205B5A]" />
                        <span className="truncate">{a.filename}</span>
                        {a.size ? <span className="text-[10px] text-[#757675] flex-shrink-0">{(a.size / 1024).toFixed(0)} KB</span> : null}
                        <button onClick={() => setAttachments((list) => list.filter((_, x) => x !== i))}
                          className="ml-auto text-[#757675] hover:text-red-500" data-testid={`letter-remove-attachment-${i}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {stage !== "compose" && (
            <>
              <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5" data-testid="letter-preview">
                <p className="text-xs text-[#757675] mb-3">Preview of the letter as it will be saved and sent</p>
                <h3 className="text-lg text-[#1A1B1A] mb-3" style={H}>{subject}</h3>
                <div className="text-sm text-[#1A1B1A] leading-relaxed [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-1 [&_p]:mb-2 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5"
                  dangerouslySetInnerHTML={{ __html: bodyHtml }} />
              </div>

              <div className="bg-white rounded-2xl border border-amber-200 p-5" data-testid="letter-confirm-panel">
                <div className="flex items-start gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <p className="text-xs text-amber-700 leading-relaxed">
                    This letter may contain clinical information. Check the recipients carefully — the email
                    contains a secure link to the letter.
                  </p>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex gap-2"><span className="text-[#757675] w-20">Patient</span><span className="text-[#1A1B1A] font-medium">{patientName}</span></div>
                  <div className="flex gap-2"><span className="text-[#757675] w-20">To</span><span className="text-[#1A1B1A] font-medium" data-testid="confirm-to">{to}</span></div>
                  {ccList.length > 0 && (
                    <div className="flex gap-2"><span className="text-[#757675] w-20">CC</span><span className="text-[#1A1B1A] font-medium" data-testid="confirm-cc">{ccList.join(", ")}</span></div>
                  )}
                  <div className="flex gap-2"><span className="text-[#757675] w-20">Subject</span><span className="text-[#1A1B1A] font-medium">{subject}</span></div>
                  <div className="flex gap-2"><span className="text-[#757675] w-20">Attachments</span><span className="text-[#1A1B1A] font-medium">{attachments.length ? attachments.map((a) => a.filename).join(", ") : "None"}</span></div>
                </div>
                <button onClick={send} disabled={busy} data-testid="letter-send-btn"
                  className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#205B5A] text-white text-sm font-medium hover:bg-[#174544] disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {busy ? "Sending..." : "Send email"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
